import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { APP_NAME } from './config/app';
import { WebSocketProvider, ToastProvider, ThemeProvider, SubscriptionProvider } from "./context";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { SubscriptionGuard } from "./components/common/SubscriptionGuard";
import { RestaurantGuard } from "./components/common/RestaurantGuard";
import { RoleBasedRoute } from "./components/common/RoleBasedRoute";
import { RestaurantInitializer } from "./components/common/RestaurantInitializer";
import { ScrollToTop } from "./components/common/ScrollToTop";
import RazorpayScriptLoader from "./components/common/RazorpayScriptLoader";
import PageLoader from "./components/common/PageLoader";

// Lazy load all page components
const Home = lazy(() => import("./pages/Home"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const LoginPage = lazy(() => import("./pages/Auth").then(module => ({ default: module.LoginPage })));
const SignupPage = lazy(() => import("./pages/Auth").then(module => ({ default: module.SignupPage })));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPassword"));
const ForgotLoginIdPage = lazy(() => import("./pages/ForgotLoginId"));
const Menu = lazy(() => import("./pages/Menu"));
const Restaurants = lazy(() => import("./pages/Restaurants"));
const ViewRestaurant = lazy(() => import("./pages/ViewRestaurant"));
const SetupRestaurant = lazy(() => import("./pages/SetupRestaurant"));
const Dishes = lazy(() => import("./pages/Dishes"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Staff = lazy(() => import("./pages/Staff"));
const Orders = lazy(() => import("./pages/Orders"));
const Tables = lazy(() => import("./pages/Tables"));
const Settings = lazy(() => import("./pages/Settings"));
const SelectSubscription = lazy(() => import("./pages/SelectSubscription"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));

const ProtectedAppRoute = ({ children, allowedRoles }) => (
  <ProtectedRoute>
    <SubscriptionGuard>
      <RestaurantGuard>
        {allowedRoles ? (
          <RoleBasedRoute allowedRoles={allowedRoles}>
            {children}
          </RoleBasedRoute>
        ) : (
          children
        )}
      </RestaurantGuard>
    </SubscriptionGuard>
  </ProtectedRoute>
);

const App = () => {
  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider>
        <ToastProvider>
          <SubscriptionProvider>
            <WebSocketProvider>
              <RestaurantInitializer>
              <Router>
                <ScrollToTop />
                <RazorpayScriptLoader />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/forgot-user-id" element={<ForgotLoginIdPage />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/menu" element={<Menu />} />

                    {/* Protected routes - only accessible when logged in */}
                    <Route path="/dashboard" element={
                      <ProtectedAppRoute allowedRoles={['owner', 'manager', 'staff']}>
                        <Navigate to="/restaurants" replace />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/setup/restaurant" element={
                      <ProtectedAppRoute allowedRoles={['owner']}>
                        <SetupRestaurant />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/restaurants/:id" element={
                      <ProtectedAppRoute allowedRoles={['owner']}>
                        <ViewRestaurant />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/restaurants" element={
                      <ProtectedAppRoute allowedRoles={['owner']}>
                        <Restaurants />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/dishes" element={
                      <ProtectedAppRoute allowedRoles={['owner', 'manager', 'staff']}>
                        <Dishes />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/expenses" element={
                      <ProtectedAppRoute allowedRoles={['owner', 'manager']}>
                        <Expenses />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/staff" element={
                      <ProtectedAppRoute allowedRoles={['owner', 'manager']}>
                        <Staff />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/tables" element={
                      <ProtectedAppRoute allowedRoles={['owner', 'manager']}>
                        <Tables />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/orders" element={
                      <ProtectedAppRoute>
                        <Orders />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedAppRoute allowedRoles={['owner', 'manager', 'staff']}>
                        <Settings />
                      </ProtectedAppRoute>
                    } />
                    <Route path="/select-subscription" element={
                      <ProtectedRoute>
                        <SubscriptionGuard>
                          <SelectSubscription />
                        </SubscriptionGuard>
                      </ProtectedRoute>
                    } />

                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Router>
            </RestaurantInitializer>
          </WebSocketProvider>
          </SubscriptionProvider>
        </ToastProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
